import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import { DraggableBlock } from "./DraggableBlock";
import { CanvasToolbar } from "./CanvasToolbar";
import { GridOverlay } from "./GridOverlay";
import { BlocksOutlinePanel } from "./BlocksOutlinePanel";
import type { Page, Block } from "@/types/proposals";
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";

interface FreeFormCanvasProps {
  currentPage?: Page;
  onSelectBlock: (block: Block) => void;
  onUpdateBlock: (blockId: string, updates: Partial<Block>) => void;
  onDeleteBlock: (blockId: string) => void;
  onReorderBlocks: (pageId: string, blocks: Block[]) => void;
  pages: Page[];
  currentPageId: string;
  onPageChange: (pageId: string) => void;
  onAddPage: () => void;
  selectedBlockIds: string[];
  onMultiSelect: (blockIds: string[]) => void;
  smartFieldData?: Record<string, any>;
}

const CANVAS_WIDTH = 816; // 8.5" at 96dpi
const CANVAS_HEIGHT = 1056; // 11" at 96dpi
const GRID_SIZE = 16;

function snapToGrid(value: number, snapEnabled: boolean): number {
  if (!snapEnabled) return value;
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

export function FreeFormCanvas({
  currentPage,
  onSelectBlock,
  onUpdateBlock,
  onDeleteBlock,
  pages,
  currentPageId,
  onPageChange,
  onAddPage,
  selectedBlockIds,
  onMultiSelect,
  smartFieldData,
}: FreeFormCanvasProps) {
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGridEnabled, setSnapToGridEnabled] = useState(true);
  const [history, setHistory] = useState<Page[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isSavingHistory, setIsSavingHistory] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 1 } }));

  // Save to history before any change
  const saveToHistory = (currentPages: Page[]) => {
    if (isSavingHistory) return; // Prevent saving while restoring
    
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(currentPages))); // Deep clone
    
    const trimmedHistory = newHistory.slice(-50); // Keep last 50 states
    setHistory(trimmedHistory);
    setHistoryIndex(trimmedHistory.length - 1);
  };

  const handleZoomFit = () => {
    if (!viewportRef.current) return;
    const containerWidth = viewportRef.current.clientWidth;
    const containerHeight = viewportRef.current.clientHeight;
    const availableWidth = containerWidth - 64;
    const availableHeight = containerHeight - 64;
    const zoomX = availableWidth / CANVAS_WIDTH;
    const zoomY = availableHeight / CANVAS_HEIGHT;
    const newZoom = Math.min(zoomX, zoomY, 1);
    setZoom(Math.round(newZoom * 10) / 10);
  };

  // Calculate responsive zoom
  useEffect(() => {
    const calculateZoom = () => {
      if (!viewportRef.current) return;
      const containerWidth = viewportRef.current.clientWidth;
      const containerHeight = viewportRef.current.clientHeight;
      const availableWidth = containerWidth - 64;
      const availableHeight = containerHeight - 64;
      const zoomX = availableWidth / CANVAS_WIDTH;
      const zoomY = availableHeight / CANVAS_HEIGHT;
      setZoom(Math.min(zoomX, zoomY, 1));
    };

    calculateZoom();
    window.addEventListener('resize', calculateZoom);
    return () => window.removeEventListener('resize', calculateZoom);
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    const block = currentPage?.blocks.find(b => b.id === active.id);
    if (!block) return;

    // Skip if no actual movement
    if (delta.x === 0 && delta.y === 0) {
      return;
    }

    // Calculate new position
    let newX = block.position.x + delta.x / zoom;
    let newY = block.position.y + delta.y / zoom;

    // Constrain to canvas bounds
    const minX = 0;
    const minY = 0;
    const maxX = CANVAS_WIDTH - block.position.width;
    const maxY = CANVAS_HEIGHT - (block.position.height === 'auto' ? 100 : block.position.height);

    newX = Math.max(minX, Math.min(newX, maxX));
    newY = Math.max(minY, Math.min(newY, maxY));

    // Apply snap to grid
    newX = snapToGrid(newX, snapToGridEnabled);
    newY = snapToGrid(newY, snapToGridEnabled);

    // Only save to history and update if position actually changed
    if (newX !== block.position.x || newY !== block.position.y) {
      saveToHistory(pages);
      
      onUpdateBlock(block.id, {
        position: { ...block.position, x: newX, y: newY }
      });
    }
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const blockType = e.dataTransfer.getData('blockType');
    if (!blockType || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = snapToGrid((e.clientX - rect.left) / zoom, snapToGridEnabled);
    const y = snapToGrid((e.clientY - rect.top) / zoom, snapToGridEnabled);

    // Set default sizes based on block type
    const defaultSizes: Record<string, { width: number; height: number }> = {
      text: { width: 600, height: 200 },
      image: { width: 400, height: 300 },
      button: { width: 200, height: 50 },
    };

    const size = defaultSizes[blockType] || { width: 200, height: 100 };

    // Trigger add block with position and size
    window.dispatchEvent(new CustomEvent('addBlockAtPosition', { 
      detail: { blockType, x, y, ...size } 
    }));
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || e.target === canvasRef.current) {
      onMultiSelect([]);
    }
  };

  const handleBlockResize = (
    blockId: string,
    newRect: { x: number; y: number; width: number; height: number }
  ) => {
    const block = currentPage?.blocks.find(b => b.id === blockId);
    if (!block) return;

    // Save history only once at the beginning of resize
    if (!isResizing) {
      saveToHistory(pages);
      setIsResizing(true);
    }

    // Constrain rect to canvas bounds
    let { x, y, width, height } = newRect;

    const minX = 0;
    const minY = 0;
    const maxX = CANVAS_WIDTH;
    const maxY = CANVAS_HEIGHT;

    // Clamp width/height first
    const minWidth = 50;
    const minHeight = 30;
    width = Math.max(minWidth, width);
    height = Math.max(minHeight, height);

    // Ensure right/bottom edges do not go past canvas
    if (x + width > maxX) {
      x = maxX - width;
    }
    if (y + height > maxY) {
      y = maxY - height;
    }

    // Ensure we don't go negative
    x = Math.max(minX, x);
    y = Math.max(minY, y);

    // Snap only position, not size, for smooth resize
    x = snapToGrid(x, snapToGridEnabled);
    y = snapToGrid(y, snapToGridEnabled);
    // Don't snap width/height during resize for continuous feel
    // width = snapToGrid(width, snapToGridEnabled);
    // height = snapToGrid(height, snapToGridEnabled);

    onUpdateBlock(blockId, {
      position: {
        ...block.position,
        x,
        y,
        width,
        height,
      },
    });
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setIsSavingHistory(true);
      const previousState = history[historyIndex - 1];
      
      // Restore pages through parent callback
      window.dispatchEvent(new CustomEvent('restoreLayout', { 
        detail: { pages: previousState } 
      }));
      
      setHistoryIndex(historyIndex - 1);
      setTimeout(() => setIsSavingHistory(false), 100);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setIsSavingHistory(true);
      const nextState = history[historyIndex + 1];
      
      window.dispatchEvent(new CustomEvent('restoreLayout', { 
        detail: { pages: nextState } 
      }));
      
      setHistoryIndex(historyIndex + 1);
      setTimeout(() => setIsSavingHistory(false), 100);
    }
  };

  // Listen for keyboard shortcuts
  useEffect(() => {
    const handleUndoEvent = () => handleUndo();
    const handleRedoEvent = () => handleRedo();
    const handleToggleGrid = () => setShowGrid(!showGrid);
    const handleZoomIn = () => setZoom(Math.min(zoom + 0.1, 2));
    const handleZoomOut = () => setZoom(Math.max(zoom - 0.1, 0.25));
    const handleResetZoom = () => setZoom(1);

    window.addEventListener('undo', handleUndoEvent);
    window.addEventListener('redo', handleRedoEvent);
    window.addEventListener('toggleGrid', handleToggleGrid);
    window.addEventListener('zoomIn', handleZoomIn);
    window.addEventListener('zoomOut', handleZoomOut);
    window.addEventListener('resetZoom', handleResetZoom);

    return () => {
      window.removeEventListener('undo', handleUndoEvent);
      window.removeEventListener('redo', handleRedoEvent);
      window.removeEventListener('toggleGrid', handleToggleGrid);
      window.removeEventListener('zoomIn', handleZoomIn);
      window.removeEventListener('zoomOut', handleZoomOut);
      window.removeEventListener('resetZoom', handleResetZoom);
    };
  }, [zoom, showGrid, historyIndex, history]);

  // Auto-constrain blocks on page load
  useEffect(() => {
    if (!currentPage) return;
    
    const offCanvasBlocks = currentPage.blocks.filter(block => {
      const { x, y, width, height } = block.position;
      const effectiveHeight = height === 'auto' ? 100 : height;
      return (
        x < 0 || 
        y < 0 || 
        x + width > CANVAS_WIDTH || 
        y + effectiveHeight > CANVAS_HEIGHT
      );
    });

    if (offCanvasBlocks.length > 0) {
      console.log(`Found ${offCanvasBlocks.length} off-canvas blocks, auto-constraining...`);
      offCanvasBlocks.forEach(block => {
        let newX = Math.max(0, Math.min(block.position.x, CANVAS_WIDTH - block.position.width));
        let newY = Math.max(0, Math.min(block.position.y, CANVAS_HEIGHT - (block.position.height === 'auto' ? 100 : block.position.height)));
        
        if (newX !== block.position.x || newY !== block.position.y) {
          onUpdateBlock(block.id, {
            position: { ...block.position, x: newX, y: newY }
          });
        }
      });
    }
  }, [currentPageId]); // Run when page changes

  // History is now saved on specific actions (drag end, etc.) instead of every block change

  const handleBringToView = (blockId: string) => {
    const block = currentPage?.blocks.find(b => b.id === blockId);
    if (!block) return;

    saveToHistory(pages);

    // Center the block on canvas
    const centerX = (CANVAS_WIDTH - block.position.width) / 2;
    const centerY = (CANVAS_HEIGHT - (block.position.height === 'auto' ? 100 : block.position.height)) / 2;

    onUpdateBlock(blockId, {
      position: { ...block.position, x: centerX, y: centerY }
    });

    onMultiSelect([blockId]);
  };

  const handleResetAllBlocks = () => {
    if (!currentPage || currentPage.blocks.length === 0) return;

    if (!confirm('This will reposition all blocks in a cascade layout. Continue?')) {
      return;
    }

    saveToHistory(pages);

    let currentY = 32;
    const margin = 16;

    currentPage.blocks.forEach((block, index) => {
      const effectiveHeight = block.position.height === 'auto' ? 100 : block.position.height;
      
      onUpdateBlock(block.id, {
        position: { 
          ...block.position, 
          x: 32,
          y: currentY 
        }
      });

      currentY += effectiveHeight + margin;
    });
  };

  const handleAlignLeft = () => {
    if (selectedBlockIds.length === 0) return;
    const minX = Math.min(...selectedBlockIds.map(id => 
      currentPage?.blocks.find(b => b.id === id)?.position.x || 0
    ));
    selectedBlockIds.forEach(id => {
      const block = currentPage?.blocks.find(b => b.id === id);
      if (block) {
        onUpdateBlock(id, { position: { ...block.position, x: minX } });
      }
    });
  };

  const handleAlignCenter = () => {
    if (selectedBlockIds.length === 0) return;
    const blocks = selectedBlockIds.map(id => currentPage?.blocks.find(b => b.id === id)).filter(Boolean);
    const minX = Math.min(...blocks.map(b => b!.position.x));
    const maxX = Math.max(...blocks.map(b => b!.position.x + b!.position.width));
    const centerX = (minX + maxX) / 2;
    
    blocks.forEach(block => {
      if (block) {
        onUpdateBlock(block.id, { 
          position: { ...block.position, x: centerX - block.position.width / 2 } 
        });
      }
    });
  };

  const handleAlignRight = () => {
    if (selectedBlockIds.length === 0) return;
    const blocks = selectedBlockIds.map(id => currentPage?.blocks.find(b => b.id === id)).filter(Boolean);
    const maxX = Math.max(...blocks.map(b => b!.position.x + b!.position.width));
    
    blocks.forEach(block => {
      if (block) {
        onUpdateBlock(block.id, { 
          position: { ...block.position, x: maxX - block.position.width } 
        });
      }
    });
  };

  return (
    <div className="flex-1 flex flex-col bg-muted/30 overflow-hidden">
      {/* Canvas Toolbar */}
      <CanvasToolbar
        zoom={zoom}
        onZoomChange={setZoom}
        onZoomFit={handleZoomFit}
        showGrid={showGrid}
        onToggleGrid={() => setShowGrid(!showGrid)}
        snapToGrid={snapToGridEnabled}
        onToggleSnap={() => setSnapToGridEnabled(!snapToGridEnabled)}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        onAlignLeft={handleAlignLeft}
        onAlignCenter={handleAlignCenter}
        onAlignRight={handleAlignRight}
        onResetAllBlocks={handleResetAllBlocks}
      />

      {/* Page Tabs */}
      <div className="border-b bg-card px-4 py-2 flex items-center justify-between">
        <Tabs value={currentPageId} onValueChange={onPageChange}>
          <TabsList>
            {pages.map((page) => (
              <TabsTrigger key={page.id} value={page.id}>
                {page.title}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button variant="outline" size="sm" onClick={onAddPage}>
          <Plus className="w-4 h-4 mr-1" />
          Add Page
        </Button>
      </div>

      {/* Canvas Viewport */}
      <div 
        ref={viewportRef}
        className="flex-1 overflow-auto p-8 canvas-print-area relative"
        onClick={handleCanvasClick}
      >
        {/* Blocks Outline Panel */}
        <BlocksOutlinePanel
          currentPage={currentPage}
          selectedBlockIds={selectedBlockIds}
          onSelectBlock={(blockId) => {
            onMultiSelect([blockId]);
            const block = currentPage?.blocks.find(b => b.id === blockId);
            if (block) onSelectBlock(block);
          }}
          onBringToView={handleBringToView}
          canvasWidth={CANVAS_WIDTH}
          canvasHeight={CANVAS_HEIGHT}
        />

        <div className="flex items-center justify-center min-h-full">
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s',
            }}
          >
            <Card
              ref={canvasRef}
              className="bg-white shadow-2xl relative overflow-hidden canvas-page"
              style={{
                width: `${CANVAS_WIDTH}px`,
                height: `${CANVAS_HEIGHT}px`,
              }}
              onDrop={handleCanvasDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {/* Grid Overlay */}
              {showGrid && <GridOverlay gridSize={GRID_SIZE} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} />}

              {/* Empty State */}
              {!currentPage || currentPage.blocks.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center text-center text-muted-foreground p-8 bg-gradient-to-br from-slate-50 to-slate-100">
                  <div className="max-w-md">
                    <div className="text-5xl mb-4">📄</div>
                    <p className="text-lg font-semibold text-foreground mb-2">Start Building Your Template</p>
                    <p className="text-sm mb-6">Drag blocks from the left panel or choose a quick-start template</p>
                    <div className="space-y-2 text-xs text-muted-foreground">
                      <p>⌘ G - Toggle grid | ⌘ Z - Undo | ⌘ Y - Redo</p>
                    </div>
                  </div>
                </div>
              ) : (
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                  {currentPage.blocks.map((block) => (
                    <DraggableBlock
                      key={block.id}
                      block={block}
                      isSelected={selectedBlockIds.includes(block.id)}
                      onSelect={(multiSelect) => {
                        if (multiSelect) {
                          onMultiSelect(
                            selectedBlockIds.includes(block.id)
                              ? selectedBlockIds.filter(id => id !== block.id)
                              : [...selectedBlockIds, block.id]
                          );
                        } else {
                          onMultiSelect([block.id]);
                          onSelectBlock(block);
                        }
                      }}
                      onUpdate={(updates) => onUpdateBlock(block.id, updates)}
                      onDelete={() => onDeleteBlock(block.id)}
                      onResize={(rect) => handleBlockResize(block.id, rect)}
                      onResizeEnd={handleResizeEnd}
                      zoom={zoom}
                      smartFieldData={smartFieldData}
                    />
                  ))}
                </DndContext>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
